import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SlipExtraction, RecentEntry } from '../../models/receiving-mobile/receiving-mobile.models';
import { FormattedPoValues } from '../../models/poData/FormattedPoValues';
import { LogEntryPhoto } from '../../models/logs/photos/LogEntryPhoto.interface';
import { LogType } from '../../models/logs/LogType.enum';
import { MobilePrintType } from '../../models/assets-data/receiving/ReceivingLogFormConfig.interface';
import { ExtractedLineItem, FallbackIdentifiers } from '../../models/poData/PackingSlipExtractionResult';
import { PoLineItem } from './po-line-items.port';
import { PoEmailData } from '../../models/poData/FormattedContacts';
import { ReceivingLogReceipt } from '../../models/poData/ReceivingStatus';
import { ReceivingItemRef } from '../../models/logs/ReceivingItemRef';
import { ContainerRow } from '../../domain/receiving-log/rules/container-items.rules';
import { environment } from '../../../environments/environment';

/**
 * Status a receiving worker assigns to a PO line item during check-off.
 * There is deliberately no 'missing' status: what's missing is dependent on
 * what arrived, so the app derives it (PO vs. logs vs. receipts vs. this
 * check-off) on the follow-up "what's still missing?" page, where the user
 * corrects the guess rather than declaring it up front.
 */
export type LineItemStatus = 'complete' | 'partial' | null;

/** A PO line item plus its in-progress receiving status (the Items step's working model). */
export type CheckableLineItem = PoLineItem & {
  status: LineItemStatus;
  receivedQty: string;
  totalQty: string;
  /**
   * New-ledger rollup, present when the row is sourced from the real PO data's
   * `lineItems` (which carries the receiving fields alongside the ordered data).
   * Absent for rows sourced from the scan/search mock port, which has no
   * rollup to offer.
   */
  poLineSequence?: number | null;
  /** The PO's ERP item number — carried through onto the receipt row at submission. */
  itemNumber?: string;
  quantityExpected?: number | null;
  quantityReceived?: number | null;
  isClosed?: boolean;
  /**
   * EFFECTIVE canceled state used everywhere (template read-only/greyed, the
   * complete/open getters, and the items-string / receipt / missing-page rules).
   * Derived from the log type: a Project treats canceled PO lines as normal, so
   * this is forced false for Project; Service keeps the ERP value. Re-derived on
   * every delivery-type toggle via LogEntryDraftStore.setLogType.
   */
  isCanceled?: boolean;
  /** The immutable ERP-canceled state, captured at row build; source for re-deriving `isCanceled`. */
  isCanceledOriginal?: boolean;
  /**
   * Immutable ERP quantities captured at row build, the source for re-deriving
   * the effective `quantityExpected` when the effective canceled state flips.
   * `quantityOrderedOriginal` is the raw ordered qty; `quantityExpectedOriginal`
   * is the canceled-reduced ERP expected (ordered − canceled, 0 for a fully
   * canceled line). A Project un-cancels ERP-canceled lines and receives them in
   * full, so its `quantityExpected` reverts to the ordered qty. See
   * [[effectiveExpectedQty]] and LogEntryDraftStore.setLogType.
   */
  quantityOrderedOriginal?: number | null;
  quantityExpectedOriginal?: number | null;
  /**
   * Prior tracked receipts behind this line's `quantityReceived` — read-only
   * reference for the parts/events already arrived. For a ragged line these are
   * the part rows (`quantityReceived: null`) that its aggregate can't show.
   * Distinct from `partsReceived`, which is THIS session's ragged entry.
   */
  receipts?: ReceivingLogReceipt[];
  /** True for a row added via the "add item" affordance — not one of the PO's own lines. */
  isOffPo?: boolean;
  /**
   * Free-text list of the specific parts that arrived on a ragged Partial line (§4).
   * When set, the row is a "parts line" (quantityReceived unknown) rather than a
   * qty line; drives the legacy page-two "still missing vs. already complete" choice.
   */
  partsReceived?: string;
  /**
   * Missing-page working value: the still-outstanding amount, pre-filled to
   * `quantityExpected − quantityReceived − receivedThisLoad` and editable. Editing
   * it to zero closes the line (a human close). String to back the numeric input.
   */
  outstandingQty?: string;
  /**
   * Missing-page free text describing WHAT is still missing on a ragged parts
   * line (e.g. "remaining parts") — note-only: it flows into the items string,
   * never into a receipt row (missing writes no receipt).
   */
  missingNotes?: string;
  /**
   * Whether the user has made an explicit missing-vs-done decision on this line
   * on the missing page. Fresh/ledgered POs pre-set this true (the derived guess
   * is trusted, correct-by-exception); legacy POs leave it false so the user
   * must specify each line (the ledger math can't be trusted there).
   */
  missingReviewed?: boolean;
  /**
   * Human close (§3, §9): the user declared this line done even though the ledger
   * math can't prove it — outstanding qty edited to zero, an earlier legacy delivery
   * completed a ragged line, or a Complete tap. Becomes `completesLine` on the receipt.
   */
  completesLine?: boolean;
};

/**
 * Stable per-flow identity for a line item, used to key its damage entry in
 * `LogEntryDraft.damageByLine`. `line` is unique within a flow (positive PO
 * line sequences, negative off-PO adds), so it survives the frequent
 * spread-copies the Items step makes of `lineItemRows`.
 */
export function lineKey(item: Pick<CheckableLineItem, 'line'>): string {
  return String(item.line);
}

/**
 * The effective expected quantity for a line under a given (effective) canceled
 * state. When we're overriding an ERP cancellation — the line was canceled in
 * the ERP (`isCanceledOriginal`) but is being treated as receivable
 * (`isCanceled === false`, i.e. a Project) — the expected reverts to the raw
 * ordered qty, since we're pretending it was never canceled; the ERP expected
 * (`quantityExpectedOriginal`) would be 0 for a fully-canceled line. Every other
 * line keeps the ERP expected (ordered − canceled). Returns null when the row
 * carries no ERP quantities (mock/search rows), so callers can leave those alone.
 */
export function effectiveExpectedQty(
  row: Pick<CheckableLineItem, 'quantityOrderedOriginal' | 'quantityExpectedOriginal' | 'isCanceledOriginal'>,
  isCanceled: boolean,
): number | null {
  const overridingCancel = !!row.isCanceledOriginal && !isCanceled;
  const expected = overridingCancel ? row.quantityOrderedOriginal : row.quantityExpectedOriginal;
  return expected ?? null;
}

/**
 * What the receiving worker does with a damaged line's goods. Drives the
 * received-vs-missing math (see [[receiving-refactor-plan]]):
 *   'Keep'   — accepted; counts as received (unchanged behavior).
 *   'Refuse' — not accepted at the dock; does NOT count as received → the item
 *              becomes missing (reason 'Refused').
 *   'Return' — accepted but going back to the vendor; does NOT count as
 *              received → missing (reason 'Returned').
 */
export type DamageDisposition = 'Refuse' | 'Return' | 'Keep';

/**
 * Per-line damage report captured on the Items step. A line has an entry in
 * `damageByLine` only while it is flagged damaged; un-flagging removes it.
 * A `disposition`, a `note`, and at least one `photo` are all required before
 * the flow can continue (the per-item "damage needs a decision + note + photo"
 * gating). `disposition` is null until the worker chooses Refuse/Return/Keep.
 *
 * Damage is a QUANTITY within the line, not a whole-line flag: `quantity` is how
 * many of the line's arrived units are damaged (defaults to the full arrived
 * amount — the common "the whole thing is trashed" case — and can be dialed down
 * when only some arrived damaged). The disposition applies to that damaged batch:
 * Keep counts them as received; Refuse/Return excludes them, so `arrived − quantity`
 * is received and `quantity` becomes missing. On ragged (parts) lines there's no
 * count, so `quantity` is unused and the disposition applies to the whole line.
 */
export interface LineDamage {
  /** How many of the line's arrived units are damaged (≥1; capped at the arrived qty). Unused on ragged lines. */
  quantity: number;
  /** Refuse / Return / Keep — null until chosen (blocks continue while null). */
  disposition: DamageDisposition | null;
  /** Free-text description of what's damaged. */
  note: string;
  /** One or more real damage photos captured for this line. */
  photos: LogEntryPhoto[];
}

/**
 * Where the current slip data came from:
 *   'api'          — real PoLookupService call (manual keypad). PO fields are real.
 *   'mock-scan'    — camera path, ReceivingMobileMockService. ALL fields are simulated.
 *   'mock-search'  — search-list selection from hardcoded mock index. Fields match the
 *                    search record but are not live from the API.
 */
export type SlipSource = 'api' | 'mock-scan' | 'mock-search';

export interface LogEntryDraft {
  currentSlip: SlipExtraction | null;
  /** Populated when the PO was resolved via PoLookupService (manual keypad path). Null for scan/search paths. */
  formattedPoValues: FormattedPoValues | null;
  /**
   * How the slip was populated. Used by templates to show (mockup) indicators on
   * data that isn't sourced from the real API.
   */
  slipSource: SlipSource;
  /**
   * Project vs Service, used for email routing and service-log rules.
   * Auto-set from formattedPoValues.jobInformation.jobType on the api path;
   * null on scan/search paths until the user selects it in log-entry.
   */
  logType: LogType | null;
  /** Real facility label from AssetsDataService (e.g. "Kent"). Set by the shell on init. */
  selectedLocationLabel: string;
  /**
   * The physical containers on this delivery as (quantity, type) pairs — a
   * delivery can be "3 box + 2 pallet", so this is a list, not a single count +
   * unit. Empty until the user taps a container type; a type never repeats (one
   * chip per type, so the structure can't express a duplicate). Submitted as
   * `containerItems`, which is also what the backend derives the log's quantity
   * string from. Write it through {@link LogEntryDraftStore.setContainers}.
   */
  containers: ContainerRow[];
  shelf: string;
  /**
   * Project multi-select shelf chips (Dock / Floor / Pipe Rack). A Project
   * delivery can be split across several staging areas, so this is a list.
   * Service uses the single `shelf` field (tech-zone auto-fill) instead.
   */
  shelfSelections: string[];
  /**
   * Free-text shelf location. On Service it's an alternative to `shelf`; on
   * Project it's an always-visible additional entry (rack codes), combined
   * with `shelfSelections`.
   */
  customShelf: string;
  notes: string;
  /**
   * Per-line damage reports, keyed by `lineKey(item)`. Only damaged lines have
   * an entry. Replaces the former whole-shipment `damaged`/`damageNote`/
   * `damagePhotoTaken` flags — damage is now reported per received line item,
   * persisted onto each line's receipt row and its damage-photo attachments.
   */
  damageByLine: Record<string, LineDamage>;
  /** Project-only: shop square footage (Details step). */
  shopSqFt: string;
  /** Free-text items entry, used alongside or instead of PO line-item check-off. */
  manualItems: string;
  /** Populated by the shipment-photos step from real device-camera captures. */
  photos: LogEntryPhoto[];
  /**
   * Packing-slip page images captured on the scan step and sent to /V1/extract-po.
   * Kept separate from `photos` (which the photos step rebuilds from its slots) so
   * they aren't clobbered; combined with `photos` at submission so they upload too.
   */
  slipPhotos: LogEntryPhoto[];
  /** Line items the AI read off the packing slip — reference only (no check-off). */
  extractedLineItems: ExtractedLineItem[];
  /** Best-effort identifiers from extract-po when no usable PO was found; null otherwise. */
  fallbackIdentifiers: FallbackIdentifiers | null;
  /**
   * The PO the packing-slip AI detected (extract-po's PoFound result), captured
   * before the user reviews/corrects it. Compared against the submitted PO at
   * submit time: if they differ, an "AI got the PO wrong" report is sent with the
   * log. Null when no AI PO was detected (manual/search entry, or NeedsFallback).
   */
  aiOriginalPo: string | null;
  /**
   * True when the packing-slip AI ran and reported NO usable PO (extract-po's
   * NeedsFallback branch), so the user had to type one in. Gates the review
   * screen's "the AI missed this PO" report — without it there's no AI claim to
   * contradict (manual/search entry never involved the AI).
   */
  aiFoundNoPo: boolean;
  /**
   * True when the user confirmed on the review screen that the PO WAS printed on
   * the slip even though the AI found none. Reported with the log at submit time
   * as a missed-PO extraction error.
   */
  aiMissedPoReported: boolean;
  /** True when the user opted out of attaching photos to this shipment. */
  photosDisabled: boolean;
  /** Whether captured photos should be attached to the notification email. */
  includePhotosInEmail: boolean;
  /**
   * Whether the SEPARATE expediting / missing-items email is sent (only relevant
   * when there ARE missing items — plain shortfalls or refused/returned goods).
   * Defaults on; toggled off from the expediting-email preview card on Review.
   */
  sendExpeditingEmail: boolean;
  /** Whether damage photos are attached to the expediting email. Defaults on. */
  includeDamagePhotosInEmail: boolean;
  /** Label print selection (Zebra / 8x11 sheet / both / none) — defaults per log type. */
  printType: MobilePrintType;
  /** Number of Zebra labels to print — per item (each physical unit gets its own QR label). */
  printQtyZebra: number;
  /** Number of 8x11 sheets to print — per item (each physical unit gets its own QR sheet). */
  printQtySheet: number;
  /**
   * "Specify per item" toggle: when true, `perItemQtyZebra`/`perItemQtySheet`
   * carry each item's own copy counts instead of the uniform per-item numbers.
   */
  perItemQtyEnabled: boolean;
  /** Per-item Zebra label copies, indexed by item (used when perItemQtyEnabled). */
  perItemQtyZebra: number[];
  /** Per-item 8x11 sheet copies, indexed by item (used when perItemQtyEnabled). */
  perItemQtySheet: number[];
  /** Chosen printer names (default from location config, overridable in Review). */
  printerZebra: string;
  printerCopier: string;
  /**
   * Master emailing switch (the large Email toggle on Review). When off, NO email
   * sends regardless of the per-email toggles. When on, the normal per-email logic
   * applies (`sendEmail` / `sendExpeditingEmail`), which retain the user's choices
   * so flipping this back on restores them.
   */
  emailingEnabled: boolean;
  /** Whether the notification email should be sent (per-email toggle in the Email sheet). */
  sendEmail: boolean;
  lastEntry: RecentEntry | null;
  /**
   * The receiving item rows from the last submit response — one per physical
   * unit, each with its GUID and the caption facts its label prints. The print
   * screen builds the QR payload from these; empty when the backend returned
   * none (prints fall back to no-QR).
   */
  lastItems: ReceivingItemRef[];

  /** Items step working model: PO line items with their in-progress receiving status. */
  lineItemRows: CheckableLineItem[];
  /** Explicit "this completes the PO" flag, separate from per-line status. */
  poComplete: boolean;
  /**
   * One-line summary of prior receipts on this PO, fetched alongside
   * lineItemRows. Stored (not left as a component field) so it survives the
   * Items step component remounting when the user steps away and back.
   */
  poHistory: string;

  /** Contacts to notify on submission (checked-off PO contacts + manually added). */
  notifyList: PoEmailData[];
  /** Email shown in the log's "email" field / starred in the notify list. */
  primaryContact: string;
  /** Comma-joined `notifyList` contacts, as consumed by the email-data rules. */
  notifyListString: string;

  /** Printer selection (default from location config, overridable in Review). */
  selectedZebraPrinter: string;
  selectedCopierPrinter: string;
  /** Print on/off toggle (Review step); distinct from `printType`'s doc-type selection. */
  printEnabled: boolean;
  /** Whether print settings have been changed from their per-log-type defaults. */
  printTouched: boolean;

  /**
   * Idempotency key (client-minted GUID) for this delivery's submission. Minted once
   * at submit time via `ensureSubmissionKey`, reused if the POST is retried so the
   * backend dedupes instead of creating a duplicate log, and used as the IndexedDB
   * key for the held photo bytes. Null until the first submit; reset by `resetFlow`.
   */
  submissionKey: string | null;
}

const INITIAL_DRAFT: LogEntryDraft = {
  currentSlip: null,
  formattedPoValues: null,
  slipSource: 'mock-scan',
  logType: null,
  selectedLocationLabel: '',
  containers: [], // nothing auto-selected; the user taps the container types
  shelf: '',
  shelfSelections: [],
  customShelf: '',
  notes: '',
  damageByLine: {},
  shopSqFt: '',
  manualItems: '',
  photos: [],
  slipPhotos: [],
  extractedLineItems: [],
  fallbackIdentifiers: null,
  aiOriginalPo: null,
  aiFoundNoPo: false,
  aiMissedPoReported: false,
  photosDisabled: false,
  includePhotosInEmail: true,
  sendExpeditingEmail: true,
  includeDamagePhotosInEmail: true,
  printType: 'both',
  printQtyZebra: 1,
  printQtySheet: 1,
  perItemQtyEnabled: false,
  perItemQtyZebra: [],
  perItemQtySheet: [],
  printerZebra: '',
  printerCopier: '',
  emailingEnabled: true,
  sendEmail: true,
  lastEntry: null,
  lastItems: [],

  lineItemRows: [],
  poComplete: false,
  poHistory: '',

  notifyList: [],
  primaryContact: '',
  notifyListString: '',

  selectedZebraPrinter: '',
  selectedCopierPrinter: '',
  // Off outside production so pilot/UAT testing doesn't fire real label prints
  // (the per-type defaults and the Review toggle can still turn it on).
  printEnabled: environment.production,
  printTouched: false,
  submissionKey: null,
};

/**
 * Per-flow draft store for the receiving mobile flow. Provided in
 * ReceivingMobileModule (not root) so each flow entry gets a fresh instance.
 * Replaces the state half of ReceivingMobileMockService.
 */
@Injectable()
export class LogEntryDraftStore {
  private readonly _draft = new BehaviorSubject<LogEntryDraft>({ ...INITIAL_DRAFT });

  public readonly draft$: Observable<LogEntryDraft> = this._draft.asObservable();

  public getSnapshot(): LogEntryDraft {
    return this._draft.getValue();
  }

  public patch(partial: Partial<LogEntryDraft>): void {
    this._draft.next({ ...this._draft.getValue(), ...partial });
  }

  /**
   * Sets the container list and drops any per-item print-copy configuration.
   *
   * Those copies are keyed by ITEM POSITION, and an item's position is a
   * function of this list: with "2 box, 2 pallet" the copies the user set on
   * PALLET 2 sit at index 3, and raising the box count to 3 slides index 3 onto
   * PALLET 1. Editing containers after configuring copies is a normal thing to
   * do (Review → back to Details → forward), so the arrays are cleared and the
   * uniform per-item quantities take over — a visible reset the user can redo,
   * rather than silently printing someone else's count.
   */
  public setContainers(containers: ContainerRow[]): void {
    this.patch({
      containers,
      perItemQtyEnabled: false,
      perItemQtyZebra: [],
      perItemQtySheet: [],
    });
  }

  /**
   * Sets the delivery type and re-derives each PO line's effective `isCanceled`
   * from its original ERP state. A Project treats canceled lines as normal
   * (isCanceled=false, so they're receivable and flow through the items-string /
   * ledger like any line); Service restores the ERP value. Toggling back and
   * forth is lossless because `isCanceledOriginal` is never mutated.
   */
  public setLogType(logType: LogType): void {
    const rows = this._draft.getValue().lineItemRows.map(r => {
      const isCanceled = logType === LogType.Project
        ? false
        : (r.isCanceledOriginal ?? r.isCanceled ?? false);
      // Overriding an ERP cancellation (Project) receives the line in full, so
      // its expected reverts to the ordered qty; keep `quantity` in step.
      const expected = effectiveExpectedQty(r, isCanceled);
      return expected != null
        ? { ...r, isCanceled, quantityExpected: expected, quantity: String(expected) }
        : { ...r, isCanceled };
    });
    this.patch({ logType, lineItemRows: rows });
  }

  public resetFlow(): void {
    const { selectedLocationLabel } = this._draft.getValue();
    this._draft.next({ ...INITIAL_DRAFT, selectedLocationLabel });
  }

  /**
   * Returns this submission's idempotency key, minting a GUID on first call and
   * reusing it thereafter so a retried submit dedupes to the same log. Reset to null
   * for the next delivery by `resetFlow`.
   */
  public ensureSubmissionKey(): string {
    const existing = this._draft.getValue().submissionKey;
    if (existing) { return existing; }
    const key = crypto.randomUUID();
    this.patch({ submissionKey: key });
    return key;
  }
}
